import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { adminApi } from '@/lib/api';

interface PlatformSettings {
  platformName: string;
  platformUrl: string;
  supportEmail: string;
  defaultCommissionRate: number;
  defaultCookieDuration: number;
  defaultHoldPeriod: number;
  minPayoutAmount: number;
  maintenanceMode: boolean;
  allowNewRegistrations: boolean;
  allowNewOrganizations: boolean;
  stripeMode: 'live' | 'test';
}

export default function AdminSettings() {
  const [settings, setSettings] = useState<PlatformSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await adminApi.getSettings();
        if (response.data.success) {
          setSettings(response.data.data.settings);
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!settings) return;

    setIsSaving(true);
    try {
      const response = await adminApi.updateSettings(settings);
      if (response.data.success) {
        toast.success('Settings saved successfully');
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string } } } };
      toast.error(err.response?.data?.error?.message || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading || !settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sage"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-display text-2xl font-bold text-charcoal">Platform Settings</h2>
        <p className="text-charcoal/70 mt-1">Configure global platform defaults</p>
      </div>

      <form onSubmit={handleSave} className="space-y-8">
        {/* General Settings */}
        <div className="rounded-xl bg-warm-white shadow-sm border border-cream">
          <div className="px-6 py-4 border-b border-cream">
            <h3 className="font-display text-lg font-semibold text-charcoal">General</h3>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Platform Name</label>
                <input
                  type="text"
                  value={settings.platformName}
                  onChange={(e) => setSettings({ ...settings, platformName: e.target.value })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Platform URL</label>
                <input
                  type="url"
                  value={settings.platformUrl}
                  onChange={(e) => setSettings({ ...settings, platformUrl: e.target.value })}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Support Email</label>
                <input
                  type="email"
                  value={settings.supportEmail}
                  onChange={(e) => setSettings({ ...settings, supportEmail: e.target.value })}
                  className="input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Default Commission Settings */}
        <div className="rounded-xl bg-warm-white shadow-sm border border-cream">
          <div className="px-6 py-4 border-b border-cream">
            <h3 className="font-display text-lg font-semibold text-charcoal">Default Commission Settings</h3>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="label">Default Commission Rate (%)</label>
                <input
                  type="number"
                  value={settings.defaultCommissionRate}
                  onChange={(e) => setSettings({ ...settings, defaultCommissionRate: Number(e.target.value) })}
                  className="input"
                  min="0"
                  max="100"
                />
              </div>
              <div>
                <label className="label">Cookie Duration (days)</label>
                <input
                  type="number"
                  value={settings.defaultCookieDuration}
                  onChange={(e) => setSettings({ ...settings, defaultCookieDuration: Number(e.target.value) })}
                  className="input"
                  min="1"
                  max="365"
                />
              </div>
              <div>
                <label className="label">Hold Period (days)</label>
                <input
                  type="number"
                  value={settings.defaultHoldPeriod}
                  onChange={(e) => setSettings({ ...settings, defaultHoldPeriod: Number(e.target.value) })}
                  className="input"
                  min="0"
                  max="90"
                />
              </div>
            </div>
            <div>
              <label className="label">Minimum Payout Amount (cents)</label>
              <input
                type="number"
                value={settings.minPayoutAmount}
                onChange={(e) => setSettings({ ...settings, minPayoutAmount: Number(e.target.value) })}
                className="input w-48"
                min="0"
              />
              <p className="text-xs text-charcoal/50 mt-1">
                Currently: <span className="font-serif font-medium">${(settings.minPayoutAmount / 100).toFixed(2)}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Access Controls */}
        <div className="rounded-xl bg-warm-white shadow-sm border border-cream">
          <div className="px-6 py-4 border-b border-cream">
            <h3 className="font-display text-lg font-semibold text-charcoal">Access Controls</h3>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="maintenanceMode"
                checked={settings.maintenanceMode}
                onChange={(e) => setSettings({ ...settings, maintenanceMode: e.target.checked })}
                className="rounded border-cream bg-warm-white text-sage focus:ring-sage"
              />
              <label htmlFor="maintenanceMode" className="text-charcoal">
                Maintenance Mode
                <span className="text-charcoal/50 text-sm ml-2">(Blocks all non-admin access)</span>
              </label>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="allowRegistrations"
                checked={settings.allowNewRegistrations}
                onChange={(e) => setSettings({ ...settings, allowNewRegistrations: e.target.checked })}
                className="rounded border-cream bg-warm-white text-sage focus:ring-sage"
              />
              <label htmlFor="allowRegistrations" className="text-charcoal">
                Allow New Registrations
              </label>
            </div>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="allowOrganizations"
                checked={settings.allowNewOrganizations}
                onChange={(e) => setSettings({ ...settings, allowNewOrganizations: e.target.checked })}
                className="rounded border-cream bg-warm-white text-sage focus:ring-sage"
              />
              <label htmlFor="allowOrganizations" className="text-charcoal">
                Allow New Organization Creation
              </label>
            </div>
          </div>
        </div>

        {/* Stripe Settings */}
        <div className="rounded-xl bg-warm-white shadow-sm border border-cream">
          <div className="px-6 py-4 border-b border-cream">
            <h3 className="font-display text-lg font-semibold text-charcoal">Stripe Configuration</h3>
          </div>
          <div className="p-6">
            <div>
              <label className="label">Stripe Mode</label>
              <select
                value={settings.stripeMode}
                onChange={(e) => setSettings({ ...settings, stripeMode: e.target.value as 'live' | 'test' })}
                className="input w-48"
              >
                <option value="test">Test Mode</option>
                <option value="live">Live Mode</option>
              </select>
              <p className="text-xs text-charcoal/50 mt-1">
                {settings.stripeMode === 'test'
                  ? 'Using test API keys - no real payments will be processed'
                  : 'Using live API keys - real payments will be processed'}
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button type="submit" disabled={isSaving} className="btn btn-primary px-8">
            {isSaving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}
